import { useEffect, useState } from 'react'
import { api } from './api.ts'

/**
 * Holds the conferência while somebody is working on it.
 *
 * Claimed when the screen opens, refreshed while it stays open, released when
 * it closes. The refresh is what makes the lease safe to keep short: a
 * pharmacist reading a difficult prescription for half an hour keeps their
 * claim, and a pharmacist whose laptop shut mid-sentence loses it without
 * anybody having to intervene.
 *
 * Releasing on unmount is a courtesy, not the mechanism. It is best-effort by
 * nature -- a closed tab, a killed browser and a dead network all skip it --
 * which is exactly why expiry cannot depend on it.
 */
export interface ConferenceClaim {
  /** Whose it is. `undefined` until the first answer arrives. */
  holder: string | undefined
  /** Whether this session may act on it, rather than only read it. */
  mine: boolean
}

/** Comfortably inside the server's lease, so a slow round trip never drops it. */
const REFRESH_MS = 60_000

/**
 * @param working false while this screen is only being read, not worked: a
 *   super admin walking an adversarial review (#541) is looking at how we
 *   judged, and must not hold the conferência away from whoever confers it.
 *   Going false releases what was held, like closing the screen does.
 */
export function useConferenceClaim(verificationId: string, working = true): ConferenceClaim {
  const [claim, setClaim] = useState<ConferenceClaim>({ holder: undefined, mine: false })

  useEffect(() => {
    if (!working) return
    let live = true

    const take = async () => {
      try {
        const answer = await api.claimConference(verificationId)
        if (live) setClaim({ holder: answer.holder ?? undefined, mine: answer.mine })
      } catch {
        // Best-effort. A conferência that cannot be claimed is still a
        // conferência somebody should be able to read.
      }
    }

    void take()
    const beat = setInterval(() => void take(), REFRESH_MS)

    return () => {
      live = false
      clearInterval(beat)
      // Only what we hold. Releasing a claim that is somebody else's would
      // hand their work away while they are still reading it.
      void api.releaseConference(verificationId).catch(() => undefined)
    }
  }, [verificationId, working])

  return claim
}
